import { useState } from "react";
import { Mail, Pencil, Phone, Plus, Star, Trash2, User } from "lucide-react";
import { genId, type Contact } from "@/components/api/companiesApi";
import { addContact, removeContact, setPrimaryContact } from "@/lib/cms";
import { Button } from "@/components/ui/button";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { ContactFormModal, type ContactFormValues } from "./ContactFormModal";

interface ContactsSectionProps {
    contacts: Contact[];
    onChange: (contacts: Contact[]) => void;
}

export function ContactsSection({ contacts, onChange }: ContactsSectionProps) {
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState<Contact | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const openAdd = () => {
        setEditing(null);
        setFormOpen(true);
    };

    const openEdit = (contact: Contact) => {
        setEditing(contact);
        setFormOpen(true);
    };

    const handleSubmit = (values: ContactFormValues) => {
        if (editing) {
            onChange(
                contacts.map((c) => (c.id === editing.id ? { ...c, ...values } : c)),
            );
        } else {
            const contact: Contact = { id: genId(), isPrimary: false, ...values };
            onChange(addContact(contacts, contact));
        }
    };

    const makePrimary = (id: string) => {
        const contact = contacts.find((c) => c.id === id);
        if (contact?.isPrimary) return; // clicking the current primary is a no-op
        onChange(setPrimaryContact(contacts, id));
    };

    const confirmDelete = () => {
        if (deletingId) onChange(removeContact(contacts, deletingId));
        setDeletingId(null);
    };

    const deletingContact = contacts.find((c) => c.id === deletingId) ?? null;

    return (
        <section className="glass-card p-5 space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="font-semibold">Contacts</h2>
                <Button size="sm" variant="outline" onClick={openAdd} className="gap-1.5">
                    <Plus className="h-4 w-4" />
                    Add contact
                </Button>
            </div>

            {contacts.length === 0 ? (
                <p className="text-sm text-muted-foreground">No contact yet.</p>
            ) : (
                <ul className="divide-y divide-border/60">
                    {contacts.map((contact) => (
                        <li key={contact.id} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                            <button
                                type="button"
                                onClick={() => makePrimary(contact.id)}
                                aria-label={
                                    contact.isPrimary
                                        ? `${contact.name} is the primary contact`
                                        : `Make ${contact.name} the primary contact`
                                }
                                aria-pressed={contact.isPrimary}
                                title={contact.isPrimary ? "Primary contact" : "Make primary"}
                                className="mt-0.5 text-muted-foreground hover:text-amber-300 transition-colors"
                            >
                                <Star
                                    className={cn(
                                        "h-4 w-4",
                                        contact.isPrimary && "fill-amber-300 text-amber-300",
                                    )}
                                />
                            </button>

                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="flex items-center gap-1.5 font-medium">
                                        <User className="h-3.5 w-3.5 text-muted-foreground" />
                                        {contact.name}
                                    </span>
                                    {contact.title && (
                                        <span className="text-xs text-muted-foreground">
                                            {contact.title}
                                        </span>
                                    )}
                                    {contact.isPrimary && (
                                        <span className="text-[10px] uppercase tracking-wide text-amber-300 font-semibold">
                                            Primary
                                        </span>
                                    )}
                                </div>
                                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                    {contact.phone && (
                                        <span className="flex items-center gap-1.5">
                                            <Phone className="h-3.5 w-3.5" />
                                            {contact.phone}
                                        </span>
                                    )}
                                    {contact.email && (
                                        <a
                                            href={`mailto:${contact.email}`}
                                            className="flex items-center gap-1.5 hover:text-foreground"
                                        >
                                            <Mail className="h-3.5 w-3.5" />
                                            {contact.email}
                                        </a>
                                    )}
                                </div>
                                {contact.description && (
                                    <p className="mt-1 text-xs text-muted-foreground">
                                        {contact.description}
                                    </p>
                                )}
                            </div>

                            <div className="flex items-center gap-1">
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-8 w-8"
                                    onClick={() => openEdit(contact)}
                                    aria-label={`Edit ${contact.name}`}
                                >
                                    <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-8 w-8 text-destructive hover:text-destructive"
                                    onClick={() => setDeletingId(contact.id)}
                                    aria-label={`Delete ${contact.name}`}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            <ContactFormModal
                open={formOpen}
                onOpenChange={setFormOpen}
                initial={editing}
                onSubmit={handleSubmit}
            />

            <AlertDialog open={deletingId !== null} onOpenChange={(open) => !open && setDeletingId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete contact?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Remove {deletingContact?.name || "this contact"} from this company?
                            {deletingContact?.isPrimary && contacts.length > 1 && (
                                <> The next contact will become primary.</>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </section>
    );
}
